import { AdminPanelSettingsOutlined, ManageAccountsOutlined, PersonOffOutlined } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key.tsx';
import type { EventContributorRole } from '@/types/dtos/contributors/response/contributor.dto';
import type { ReactNode } from 'react';

/** Form-only value for “no crew role”; mapped to `undefined` when creating the event. */
export const CREATE_EVENT_NO_ROLE_VALUE = 'no_role' as const;
export type CreateEventRoleFieldValue = EventContributorRole | typeof CREATE_EVENT_NO_ROLE_VALUE;

const roleToIcon = (value: CreateEventRoleFieldValue): ReactNode => {
  switch (value) {
    case 'owner':
      return <AdminPanelSettingsOutlined />;
    case 'event_manager':
      return <ManageAccountsOutlined />;
    case CREATE_EVENT_NO_ROLE_VALUE:
      return <PersonOffOutlined />;
    default:
      return null;
  }
};

interface EventContributorRoleInfoProps {
  value: CreateEventRoleFieldValue;
  showDescription?: boolean;
}

const EventContributorRoleInfo: React.FC<EventContributorRoleInfoProps> = ({ value, showDescription = true }) => {
  const t = useTranslations();
  const title =
    value === CREATE_EVENT_NO_ROLE_VALUE ? t('event.noRole') : t(`common.roleLabels.${value}` as TranslationKey);
  const description: string =
    value === CREATE_EVENT_NO_ROLE_VALUE
      ? t('event.noRoleDescription')
      : t(`common.roleSubtitles.${value}` as TranslationKey);

  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Stack sx={{ fontSize: 24, display: 'flex', alignItems: 'center', lineHeight: 1 }}>{roleToIcon(value)}</Stack>

      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
        {showDescription && (
          <Typography variant="body2" color="text.primary" sx={{ fontSize: '0.875rem' }}>
            {description}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

export default EventContributorRoleInfo;
